import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import Links from '@/shared/constants/navegacao';
import { ColaboradorProfileFields } from './colaborador-profile';
import { DefensorProfileFields } from './defensor-profile';
import { EstagiarioProfileFields } from './estagiario-profile';
import { PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import { PersonProfilesFormMethods } from './profile.types';
import {
  OPERATIONAL_PROFILE_LABELS,
  OperationalProfileEntry,
  OperationalProfileKind,
  OperationalProfileRemoval,
  activateOperationalProfileEntry,
  addOperationalProfileEntry,
  buildPerfisOperacionaisFromForm,
  getOperationalProfileTabLabel,
  prepareOperationalProfileForEdit,
  removeOperationalProfileEntry,
  resolveAllowedAddKinds,
  sortOperationalProfiles,
  updateOperationalProfileEntry,
} from './operational-profiles.utils';
import { Dropdown, Tab, Tabs, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface OperationalProfilesSectionProps {
  methods: PersonProfilesFormMethods;
  pageTipo: 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';
  podeVerDefensor: boolean;
  podeVerEstagiario: boolean;
  podeVerColaborador: boolean;
}

function ProfileTabTitle({ entry }: { entry: OperationalProfileEntry }) {
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span className="text-truncate" style={{ maxWidth: '220px' }}>
        {getOperationalProfileTabLabel(entry)}
      </span>
      {entry.ativo ? (
        <span className="badge bg-success">Ativo</span>
      ) : entry.isDraft ? (
        <span className="badge bg-info text-dark">Novo</span>
      ) : (
        <span className="badge bg-secondary">Inativo</span>
      )}
    </span>
  );
}

export function OperationalProfilesSection({
  methods,
  pageTipo,
  podeVerDefensor,
  podeVerEstagiario,
  podeVerColaborador,
}: OperationalProfilesSectionProps) {
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  const perfisWatch = methods.watch('perfisOperacionais');
  const defensorIdWatch = methods.watch('defensor.id');

  useEffect(() => {
    const current = methods.getValues('perfisOperacionais');
    if (!current?.length) {
      const built = buildPerfisOperacionaisFromForm(methods.getValues() as PersonFormDto);
      if (built.length) {
        methods.setValue('perfisOperacionais', built, { shouldDirty: false });
      }
    }
  }, [methods]);

  const perfis = useMemo(() => {
    const values = methods.getValues() as PersonFormDto;
    const lista = (perfisWatch as OperationalProfileEntry[] | undefined)
      ?? values.perfisOperacionais
      ?? buildPerfisOperacionaisFromForm(values);
    return sortOperationalProfiles(lista);
  }, [methods, perfisWatch]);

  const addOptions = resolveAllowedAddKinds(pageTipo, {
    defensor: podeVerDefensor,
    estagiario: podeVerEstagiario,
    colaborador: podeVerColaborador,
  });

  const canShowSection =
    pageTipo !== 'usuario' &&
    (podeVerDefensor || podeVerEstagiario || podeVerColaborador);

  const persistPerfis = useCallback(
    (lista: OperationalProfileEntry[]) => {
      methods.setValue('perfisOperacionais', sortOperationalProfiles(lista), {
        shouldDirty: true,
      });
    },
    [methods],
  );

  const appendRemocao = useCallback(
    (remocao?: OperationalProfileRemoval) => {
      if (!remocao) return;
      const atuais = methods.getValues('perfisOperacionaisRemovidos') ?? [];
      const jaExiste = atuais.some((item) => item.kind === remocao.kind && item.id === remocao.id);
      if (jaExiste) return;
      methods.setValue('perfisOperacionaisRemovidos', [...atuais, remocao], {
        shouldDirty: true,
      });
    },
    [methods],
  );

  const clearSingularProfileField = useCallback(
    (entry: OperationalProfileEntry) => {
      methods.setValue(entry.kind, undefined, { shouldDirty: true });
    },
    [methods],
  );

  const loadTabIntoForm = useCallback(
    (entry: OperationalProfileEntry) => {
      methods.setValue(entry.kind, prepareOperationalProfileForEdit(entry), {
        shouldDirty: false,
      });
    },
    [methods],
  );

  const syncTabToPerfis = useCallback(
    (localKey: string | null) => {
      if (!localKey) return;
      const entry = perfis.find((item) => item.localKey === localKey);
      if (!entry) return;

      const data = methods.getValues(entry.kind);
      if (!data) return;

      persistPerfis(updateOperationalProfileEntry(perfis, localKey, data));
    },
    [methods, perfis, persistPerfis],
  );

  const defaultTabKey = useMemo(() => {
    const active = perfis.find((item) => item.ativo) ?? perfis[0];
    return active?.localKey ?? null;
  }, [perfis]);

  const effectiveActiveKey = useMemo(() => {
    if (!perfis.length) return null;
    if (activeTabKey && perfis.some((item) => item.localKey === activeTabKey)) {
      return activeTabKey;
    }
    return defaultTabKey;
  }, [activeTabKey, perfis, defaultTabKey]);

  useEffect(() => {
    if (!perfis.length) {
      setActiveTabKey(null);
      return;
    }
    setActiveTabKey((prev) => {
      if (prev && perfis.some((item) => item.localKey === prev)) {
        return prev;
      }
      return defaultTabKey;
    });
  }, [perfis, defaultTabKey]);

  useEffect(() => {
    if (!effectiveActiveKey) return;
    const entry = perfis.find((item) => item.localKey === effectiveActiveKey);
    if (entry) loadTabIntoForm(entry);
  }, [effectiveActiveKey, perfis, loadTabIntoForm]);

  const handleTabSelect = (key: string | null) => {
    if (!key || key === effectiveActiveKey) return;
    syncTabToPerfis(effectiveActiveKey);
    setActiveTabKey(key);
  };

  const handleAddKind = (kind: OperationalProfileKind) => {
    syncTabToPerfis(effectiveActiveKey);
    const atualizados = addOperationalProfileEntry(perfis, kind);
    persistPerfis(atualizados);
    const novo = atualizados.find((item) => item.isDraft && item.kind === kind && item.ativo);
    if (novo) {
      setActiveTabKey(novo.localKey);
    }
  };

  const handleConfirmDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!deleteKey) return;
    syncTabToPerfis(effectiveActiveKey);

    const alvo = perfis.find((item) => item.localKey === deleteKey);
    const { perfis: lista, remocao } = removeOperationalProfileEntry(perfis, deleteKey);
    persistPerfis(lista);
    appendRemocao(remocao);

    if (alvo && (alvo.ativo || alvo.isDraft)) {
      clearSingularProfileField(alvo);
    }

    if (deleteKey === effectiveActiveKey) {
      const proximo = lista.find((item) => item.ativo) ?? lista[0];
      setActiveTabKey(proximo?.localKey ?? null);
    }
    setDeleteKey(null);
  };

  const handleActivate = (localKey: string) => {
    syncTabToPerfis(effectiveActiveKey);
    const atualizados = activateOperationalProfileEntry(perfis, localKey);
    persistPerfis(atualizados);
    setActiveTabKey(localKey);
  };

  if (!canShowSection) {
    return null;
  }

  const hasOpenDraft = perfis.some((item) => item.isDraft);
  const selectedEntry = perfis.find((item) => item.localKey === effectiveActiveKey);
  const canExcluirSelected = !!selectedEntry;
  const selectedDefensorId =
    selectedEntry?.kind === 'defensor' ? selectedEntry.id ?? defensorIdWatch : undefined;

  const renderProfileFields = (entry: OperationalProfileEntry) => {
    const podeVer =
      (entry.kind === 'defensor' && podeVerDefensor) ||
      (entry.kind === 'estagiario' && podeVerEstagiario) ||
      (entry.kind === 'colaborador' && podeVerColaborador);

    if (!podeVer) return null;

    return (
      <fieldset disabled={!(entry.ativo || entry.isDraft)}>
        {entry.kind === 'defensor' && <DefensorProfileFields methods={methods} />}
        {entry.kind === 'estagiario' && <EstagiarioProfileFields methods={methods} />}
        {entry.kind === 'colaborador' && <ColaboradorProfileFields methods={methods} />}
      </fieldset>
    );
  };

  return (
    <div className="card mb-3">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="fw-semibold">Perfis operacionais</span>
        <div className="d-flex flex-wrap gap-2">
          {selectedEntry?.kind === 'defensor' && podeVerDefensor && (
            <>
              <Link
                to={`${Links.Vinculos_Index}?tipo=defensor`}
                className="btn btn-sm btn-outline-success"
                target="_blank"
              >
                Vínculos
              </Link>
              {selectedDefensorId ? (
                <Link
                  to={`${Links.Defensor_Timeline}?id=${selectedDefensorId}`}
                  className="btn btn-sm btn-outline-info"
                  target="_blank"
                >
                  Linha do tempo
                </Link>
              ) : null}
            </>
          )}
          {selectedEntry && !selectedEntry.ativo && !selectedEntry.isDraft && (
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={() => handleActivate(selectedEntry.localKey)}
              disabled={hasOpenDraft}
              title={hasOpenDraft ? 'Conclua o perfil em edição antes de ativar outro' : undefined}
            >
              Ativar perfil
            </Button>
          )}
          {canExcluirSelected && selectedEntry && (
            <Button
              variant="danger"
              size="sm"
              type="button"
              onClick={() => setDeleteKey(selectedEntry.localKey)}
            >
              Excluir perfil
            </Button>
          )}
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-primary"
              size="sm"
              disabled={addOptions.length === 0 || hasOpenDraft}
              title={hasOpenDraft ? 'Conclua o perfil em edição antes de adicionar outro' : undefined}
            >
              Adicionar perfil
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {addOptions.map((kind) => (
                <Dropdown.Item key={kind} as="button" type="button" onClick={() => handleAddKind(kind)}>
                  {OPERATIONAL_PROFILE_LABELS[kind]}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="card-body">
        {perfis.length === 0 ? (
          <p className="text-muted small mb-0">
            Nenhum perfil cadastrado. Use &quot;Adicionar perfil&quot; para incluir defensor, estagiário ou colaborador.
          </p>
        ) : (
          <Tabs
            id="perfis-operacionais-tabs"
            activeKey={effectiveActiveKey ?? undefined}
            onSelect={handleTabSelect}
            className="mb-3"
          >
            {perfis.map((entry) => (
              <Tab
                key={entry.localKey}
                eventKey={entry.localKey}
                title={<ProfileTabTitle entry={entry} />}
              >
                {entry.localKey === effectiveActiveKey && (
                  <div className="pt-3">
                    {renderProfileFields(entry)}
                  </div>
                )}
              </Tab>
            ))}
          </Tabs>
        )}
      </div>

      <ModalExcluir
        show={!!deleteKey}
        handleHide={() => setDeleteKey(null)}
        handleExcluir={handleConfirmDelete}
        mensagemPersonalizada={
          perfis.find((item) => item.localKey === deleteKey)?.isDraft
            ? 'Remover este perfil não salvo da lista?'
            : 'Excluir este perfil permanentemente? A exclusão será confirmada ao salvar a pessoa.'
        }
      />
    </div>
  );
}
